/**
 * clock widget class
 */
export default class ClockWidget {

    /**
     * constructs a new instance
     * @param {HTMLElement} main main element
     * @param {HTMLElement} host clock host element
     * @param {String} csc CSS class for clock element
     */
    constructor(main, host, csc) {
        this.main = main;
        this.host = host;
        this.clockSize = 0;
        this.cvWidth = 0;
        this.cvHeight = 0;
        this.displaySeconds = true;
        const canvas = document.createElement('canvas');
        canvas.className = csc || '';
        this.canvas = canvas;
        this.host.appendChild(canvas);
        const self = this;
        const observer = new ResizeObserver( (entries) => {
            for ( let entry of entries ) {
                self._onResize(entry);
            }
        });
        observer.observe(host, { box: 'border-box' });
        window.setInterval( () => {
            self._drawClock();
        }, 1000);
        this._onResize();
    }

    /**
     * sets a new width of the clock host element
     * @param {String} width CSS width specification
     */
    setWidth(width) {
        if ( this.host ) {
            this.host.style.width = width || '';
        }
    }

    /**
     * sets a new height of the clock host element
     * @param {String} height CSS height spcification
     */
    setHeight(height) {
        if ( this.host ) {
            this.host.style.height = height || '';
        }
    }

    /**
     * changes the "display seconds" attribute
     * @param {Boolean} s new value
     */
    setDisplaySeconds(s) {
        const ds = !!s;
        if ( this.displaySeconds !== ds ) {
            this.displaySeconds = ds;
            this._drawClock();
        }
    }

    /**
     * resize handler
     * @param {ResizeObserverEntry} entry the resize observer entry
     */
    _onResize(entry) {
        if ( entry && entry.contentRect ) {
            const rect = entry.contentRect;
            console.debug("Clock host element resized to ", rect);
            this._layout(rect.width, rect.height);
        }
    }

    /**
     * updates the layout
     * @param {Number} width new width in pixels
     * @param {Number} height new height in pixels
     */
    _layout(width, height) {
        if ( typeof width !== 'number' || Number.isNaN(width) ) {
            console.warn(`Invalid width parameter: ${width}!`);
            return;
        }
        if ( typeof height !== 'number' || Number.isNaN(height) ) {
            console.warn(`Invalid height parameter: ${height}!`);
            return;
        }
        this.clockSize = Math.min(width, height);
        this.cvWidth = width;
        this.cvHeight = height;
        if ( this.canvas ) {
            // we must set canvas' attributes! *NOT* CSS properties... 
            this.canvas.width = width;
            this.canvas.height = height;
            this._drawClock();
        }
    }

    /**
     * draws the clock
     */
    _drawClock() {
        if ( this.canvas && this.clockSize > 0 ) {
            const style = window.getComputedStyle(this.host);
            const color = style.color;
            const context = this.canvas.getContext('2d');
            const radius = this.clockSize * 0.45;
            // reset current transformation matrix to the identity matrix
            context.setTransform(1, 0, 0, 1, 0, 0);
            // move to the middle point
            context.translate(this.cvWidth / 2, this.cvHeight / 2);
            this._drawFace(context, radius, color);
            this._drawNumbers(context, radius, color);
            this._drawTime(context, radius, color);
        }
    }

    /**
     * draws the "face" of the clock
     * @param {CanvasRenderingContext2D} context drawing context
     * @param {Number} radius clock radius
     * @param {String} color foreground color
     */
    _drawFace(context, radius, color) {
        // erase background
        context.clearRect(-radius, -radius, 2 * radius, 2 * radius);
        context.beginPath();
        context.arc(0, 0, radius, 0, 2 * Math.PI);
        context.fillStyle = 'transparent';
        context.fill();
        context.beginPath();
        context.fillStyle = color;
        context.fill();
    }

    /**
     * draws the numbers of the clock; draws "ticks" if the clock is too small for text
     * @param {CanvasRenderingContext2D} context drawing context
     * @param {Number} radius clock radius
     * @param {String} color foreground color
     */
     _drawNumbers(context, radius, color) {
        const fsz = radius * 0.15;
        const dtx = fsz > 9;
        if ( dtx ) {
            context.font = fsz + 'px sans-serif';
            context.textBaseline = 'middle';
            context.textAlign = 'center';
        }
        for ( let num = 1; num <= 12; ++num ) {
            const ang = num * Math.PI / 6;
            context.rotate(ang);
            context.translate(0, -radius * 0.85);
            if ( dtx ) {
                context.rotate(-ang);
                context.fillText(num.toString(), 0, 0);
                context.rotate(ang);
            } else {
                context.beginPath();
                context.arc(0, 0, radius * 0.05, 0, 2 * Math.PI);
                context.fillStyle = color;
                context.fill();
            }
            context.translate(0, radius * 0.85);
            context.rotate(-ang);
        }
    }

    /**
     * draws the "hands" of the clock
     * @param {CanvasRenderingContext2D} context drawing context
     * @param {Number} radius clock radius
     * @param {String} color foreground color
     */
     _drawTime(context, radius, color) {
        const now = new Date();
        let hour = now.getHours();
        let minute = now.getMinutes();
        let second = now.getSeconds();
        context.strokeStyle = color;
        // hour
        hour = hour % 12;
        hour = (hour * Math.PI / 6) + (minute * Math.PI / (6 * 60)) + (second * Math.PI / (360 * 60));
        this._drawHand(context, hour, radius * 0.5, radius * 0.07, color);
        // minute
        minute = (minute * Math.PI / 30) + (second * Math.PI / (30 * 60));
        this._drawHand(context, minute, radius * 0.75, radius * 0.07, color);
        if ( this.displaySeconds ) {
            // second
            second = (second * Math.PI / 30);
            this._drawHand(context, second, radius * 0.85, radius * 0.02, color);
        }
    }

    /**
     * draws one "hand"
     * @param {CanvasRenderingContext2D} context drawing context
     * @param {Number} pos the position
     * @param {Number} length the length of the hand
     * @param {Number} width the width of the hand
     * @param {String} color foreground color
     */
     _drawHand(context, pos, length, width, color) {
        context.beginPath();
        context.lineWidth = width;
        context.lineCap = 'round';
        context.moveTo(0, 0);
        context.rotate(pos);
        context.lineTo(0, -length);
        context.stroke();
        context.rotate(-pos);
    }
}
